import { IBalusterGeometry, IRailingGeometry, IVec3 } from '@stair-craft/common/util';

import { clearBalusterSpacing } from '../shared-baluster-count/baluster-count';
import { gapFillers } from '../shared-settle-railings/rail-gaps';

/** A rectangular opening in world plan coordinates. */
export interface IOpeningRect {
    readonly x1: number;
    readonly z1: number;
    readonly x2: number;
    readonly z2: number;
}

/** Points closer than this count as one place (mm). */
const SAME_PLACE: number = 0.5;

function isCorner(opening: IOpeningRect, point: IVec3): boolean {
    const onX: boolean = Math.abs(point.x - opening.x1) < SAME_PLACE || Math.abs(point.x - opening.x2) < SAME_PLACE;
    const onZ: boolean = Math.abs(point.z - opening.z1) < SAME_PLACE || Math.abs(point.z - opening.z2) < SAME_PLACE;
    return onX && onZ;
}

function samePlace(a: IVec3, b: IVec3): boolean {
    return Math.abs(a.x - b.x) < SAME_PLACE && Math.abs(a.z - b.z) < SAME_PLACE;
}

/** Moves an end of the edge: out of the opening by `out`, and past an opening corner by `out` along the edge. */
function moveEnd(point: IVec3, normal: IVec3, along: IVec3, out: number, extend: boolean): IVec3 {
    const reach: number = extend ? out : 0;
    return { x: point.x + normal.x * out + along.x * reach, y: point.y, z: point.z + normal.z * out + along.z * reach };
}

/**
 * Sets one straight upper railing on the slab: its line moves out of the opening by half a post,
 * so the posts and balusters stand wholly on the slab with their inner faces flush with the
 * opening edge. An end at an opening corner reaches half a post further, where the railing of the
 * neighbouring edge ends too — one corner post serves both.
 */
function moveOntoSlab(railing: IRailingGeometry, opening: IOpeningRect): void {
    const points: IVec3[] = railing.handrail.points;
    if (points.length !== 2) {
        return;
    }
    const [start, end]: IVec3[] = points;
    const length: number = Math.hypot(end.x - start.x, end.z - start.z);
    if (length < SAME_PLACE) {
        return;
    }
    const along: IVec3 = { x: (end.x - start.x) / length, y: 0, z: (end.z - start.z) / length };
    const alongX: boolean = Math.abs(along.x) > Math.abs(along.z);
    const centerX: number = (opening.x1 + opening.x2) / 2;
    const centerZ: number = (opening.z1 + opening.z2) / 2;
    const normal: IVec3 = alongX ? { x: 0, y: 0, z: Math.sign(start.z - centerZ) } : { x: Math.sign(start.x - centerX), y: 0, z: 0 };
    const out: number = railing.postWidth / 2;
    const backward: IVec3 = { x: -along.x, y: 0, z: -along.z };

    const movedStart: IVec3 = moveEnd(start, normal, backward, out, isCorner(opening, start));
    const movedEnd: IVec3 = moveEnd(end, normal, along, out, isCorner(opening, end));
    railing.handrail.points = [movedStart, movedEnd];
    railing.posts = railing.posts.map((post: IBalusterGeometry) => {
        const target: IVec3 = samePlace(post.position, start) ? movedStart : movedEnd;
        return { ...post, position: { x: target.x, y: post.position.y, z: target.z } };
    });
    railing.balusters = railing.balusters.map((baluster: IBalusterGeometry) => ({
        ...baluster,
        position: { x: baluster.position.x + normal.x * out, y: baluster.position.y, z: baluster.position.z + normal.z * out },
    }));
}

/**
 * Spreads the balusters of a railing between its two end posts again, evenly and as few as keep
 * every clear gap within `limit`: the move onto the slab lengthened the run between the posts.
 */
function respreadBalusters(railing: IRailingGeometry, limit: number): void {
    if (railing.posts.length < 2) {
        return;
    }
    const floor: number = railing.posts[0].position.y;
    railing.balusters = [];
    railing.balusters = gapFillers(railing, limit, (): number => floor);
}

/**
 * Keeps a railing inside the room: an end at a wall stops with its post's outer face on the wall,
 * not half a post into it — there is no neighbouring railing past a wall to share a post with.
 */
function keepInsideRoom(railing: IRailingGeometry, room: IRoomSize): void {
    const half: number = railing.postWidth / 2;
    const inside: (point: IVec3) => IVec3 = (point: IVec3): IVec3 => ({
        x: Math.min(Math.max(point.x, half), room.width - half),
        y: point.y,
        z: Math.min(Math.max(point.z, half), room.length - half),
    });
    railing.handrail.points = railing.handrail.points.map(inside);
    railing.posts = railing.posts.map((post: IBalusterGeometry) => ({ ...post, position: inside(post.position) }));
}

/** The plan size of the room: x runs over its width, z over its length (mm). */
export interface IRoomSize {
    readonly width: number;
    readonly length: number;
}

/**
 * Puts the railings around a rectangular opening on the slab instead of astride its edge, and
 * keeps one post where two of them meet at a corner. The clear gaps stay within the set baluster
 * spacing, capped by the norm.
 */
export function placeUpperRailingsOnSlab(
    railings: readonly IRailingGeometry[],
    opening: IOpeningRect,
    balusterSpacing: number,
    room: IRoomSize
): void {
    const placed: IVec3[] = [];
    const limit: number = clearBalusterSpacing(balusterSpacing);
    for (const railing of railings) {
        moveOntoSlab(railing, opening);
        keepInsideRoom(railing, room);
        respreadBalusters(railing, limit);
        railing.posts = railing.posts.filter((post: IBalusterGeometry) => {
            if (placed.some((other: IVec3) => samePlace(other, post.position))) {
                return false;
            }
            placed.push(post.position);
            return true;
        });
    }
}

/** The opening of a config as a world plan rectangle. */
export function openingRectOf(config: { openingX: number; openingZ: number; openingWidth: number; openingLength: number }): IOpeningRect {
    return {
        x1: config.openingX,
        z1: config.openingZ,
        x2: config.openingX + config.openingWidth,
        z2: config.openingZ + config.openingLength,
    };
}
